import { useCallback, useMemo, useState, memo } from 'react';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  ReactFlow,
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  Position,
  Handle,
  NodeProps,
  useReactFlow,
  ReactFlowProvider,
  Panel,
  MarkerType,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  X,
  Maximize2,
  Minimize2,
  Focus,
  Info,
  Hash,
  Percent,
  DollarSign,
  Type,
  ToggleLeft,
  Calculator,
  List,
  Layers,
  Search,
  Edit,
} from 'lucide-react';
import { extractVariables, detectCircularDependencies } from '../utils/formula-parser';
import { Variable } from '../types/model.types';

// ─── Types ────────────────────────────────────────────────────────────

interface DependencyGraphProps {
  variables: Variable[];
  onFullscreenChange?: (isFullscreen: boolean) => void;
  onAddVariable?: () => void;
  onEditVariable?: (variable: Variable) => void;
  onDeleteVariable?: (variableId: string) => void;
  onRefresh?: () => void;
}

interface CustomNodeData {
  variable: Variable;
  isSelected: boolean;
  isConnected: boolean;
  isDimmed: boolean;
  isCircular: boolean;
  isEditing: boolean;
  dependsOn: string[];
  dependedBy: string[];
  [key: string]: unknown;
}

// ─── Constants ────────────────────────────────────────────────────────

const TYPE_COLORS: Record<string, { bg: string; border: string; text: string }> = {
  NUMBER: { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  PERCENTAGE: { bg: '#eff6ff', border: '#3b82f6', text: '#1d4ed8' },
  CURRENCY: { bg: '#fefce8', border: '#eab308', text: '#a16207' },
  TEXT: { bg: '#f9fafb', border: '#6b7280', text: '#374151' },
  BOOLEAN: { bg: '#f0fdf4', border: '#22c55e', text: '#15803d' },
  CALCULATED: { bg: '#ecfdf5', border: '#10b981', text: '#047857' },
  ENUM: { bg: '#eef2ff', border: '#6366f1', text: '#4338ca' },
  TIERED: { bg: '#faf5ff', border: '#a855f7', text: '#7e22ce' },
  LOOKUP: { bg: '#ecfeff', border: '#06b6d4', text: '#0e7490' },
};

const TYPE_ICONS: Record<string, typeof Hash> = {
  NUMBER: Hash,
  PERCENTAGE: Percent,
  CURRENCY: DollarSign,
  TEXT: Type,
  BOOLEAN: ToggleLeft,
  CALCULATED: Calculator,
  ENUM: List,
  TIERED: Layers,
  LOOKUP: Search,
};

const TYPE_LABELS: Record<string, string> = {
  NUMBER: 'Numero',
  PERCENTAGE: 'Porcentaje',
  CURRENCY: 'Moneda',
  TEXT: 'Texto',
  BOOLEAN: 'Booleano',
  CALCULATED: 'Calculada',
  ENUM: 'Enum',
  TIERED: 'Escalonada',
  LOOKUP: 'Lookup',
};

const HORIZONTAL_SPACING = 240;
const VERTICAL_SPACING = 130;

// ─── Layer computation ────────────────────────────────────────────────

function computeLayerAssignment(variables: Variable[]): Map<string, number> {
  const codeToVar = new Map(variables.filter((v) => v.code).map((v) => [v.code!, v]));
  const layers = new Map<string, number>();

  const depsMap = new Map<string, string[]>();
  for (const v of variables) {
    if (!v.code) continue;
    if (v.formula) {
      const deps = extractVariables(v.formula).filter((d) => codeToVar.has(d) && d !== v.code);
      depsMap.set(v.code, deps);
    } else {
      depsMap.set(v.code, []);
    }
  }

  const assigned = new Set<string>();
  let currentLayer = 0;
  const maxIterations = variables.length + 1;

  for (let iter = 0; iter < maxIterations; iter++) {
    const layerCodes: string[] = [];
    for (const v of variables) {
      if (!v.code || assigned.has(v.code)) continue;
      const deps = depsMap.get(v.code) || [];
      if (deps.every((d) => assigned.has(d))) {
        layerCodes.push(v.code);
      }
    }
    if (layerCodes.length === 0) break;
    for (const code of layerCodes) {
      layers.set(code, currentLayer);
      assigned.add(code);
    }
    currentLayer++;
  }

  for (const v of variables) {
    if (v.code && !layers.has(v.code)) {
      layers.set(v.code, currentLayer);
    }
  }

  return layers;
}

// ─── Custom Node ──────────────────────────────────────────────────────

const CustomNode = memo(({ data }: NodeProps<Node<CustomNodeData>>) => {
  const { variable, isSelected, isConnected, isDimmed, isCircular, isEditing } = data;
  const colors = TYPE_COLORS[variable.variableType] || TYPE_COLORS.TEXT;
  const Icon = TYPE_ICONS[variable.variableType] || Hash;

  const opacity = isDimmed ? 0.2 : 1;
  const scale = isSelected || isEditing ? 1.08 : 1;
  const shadow = isEditing
    ? `0 0 0 3px #3b82f6, 0 0 12px 2px rgba(59,130,246,0.3), 0 8px 25px -5px rgba(0,0,0,0.2)`
    : isSelected
      ? `0 0 0 3px ${isCircular ? '#ef4444' : colors.border}, 0 8px 25px -5px rgba(0,0,0,0.2)`
      : isConnected
        ? `0 0 0 2px ${isCircular ? '#fca5a5' : colors.border}80, 0 4px 15px -3px rgba(0,0,0,0.1)`
        : '0 1px 3px rgba(0,0,0,0.08)';

  const borderColor = isEditing ? '#3b82f6' : isCircular ? '#ef4444' : colors.border;

  return (
    <div
      style={{
        opacity,
        transform: `scale(${scale})`,
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{ background: borderColor, width: 8, height: 8 }}
      />
      <div
        style={{
          background: isCircular ? '#fef2f2' : colors.bg,
          border: `2px solid ${borderColor}`,
          borderRadius: '10px',
          padding: '10px 14px',
          minWidth: '140px',
          maxWidth: '200px',
          boxShadow: shadow,
        }}
      >
        {/* Header row: icon + code */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
          <Icon size={14} style={{ color: isCircular ? '#ef4444' : colors.text, flexShrink: 0 }} />
          <span
            style={{
              fontFamily: 'ui-monospace, monospace',
              fontSize: '12px',
              fontWeight: 700,
              color: isCircular ? '#ef4444' : colors.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {variable.code}
          </span>
        </div>
        {/* Name */}
        <div
          style={{
            fontSize: '11px',
            color: '#64748b',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {variable.name}
        </div>
        {/* Type badge */}
        <div style={{ marginTop: '6px' }}>
          <span
            style={{
              display: 'inline-block',
              fontSize: '9px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '2px 6px',
              borderRadius: '4px',
              background: isCircular ? '#fee2e2' : `${colors.border}18`,
              color: isCircular ? '#dc2626' : colors.text,
            }}
          >
            {isCircular ? 'Circular' : TYPE_LABELS[variable.variableType] || variable.variableType}
          </span>
          {isEditing && (
            <span
              style={{
                display: 'inline-block',
                marginLeft: '4px',
                fontSize: '9px',
                fontWeight: 600,
                padding: '2px 6px',
                borderRadius: '4px',
                background: '#3b82f6',
                color: 'white',
                animation: 'pulse 2s infinite',
              }}
            >
              Editando
            </span>
          )}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: borderColor, width: 8, height: 8 }}
      />
    </div>
  );
});

CustomNode.displayName = 'CustomNode';

const nodeTypes = { custom: CustomNode };

// ─── Detail Panel (read + inline edit) ───────────────────────────────

const VAR_TYPE_OPTIONS = [
  { value: 'NUMBER', label: 'Numero' },
  { value: 'PERCENTAGE', label: 'Porcentaje' },
  { value: 'CURRENCY', label: 'Moneda' },
  { value: 'CALCULATED', label: 'Calculada' },
  { value: 'BOOLEAN', label: 'Booleano' },
  { value: 'ENUM', label: 'Enum' },
  { value: 'TIERED', label: 'Escalonada' },
  { value: 'LOOKUP', label: 'Lookup' },
];

function PanelField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: '10px' }}>
      <div
        style={{
          fontSize: '10px',
          fontWeight: 600,
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '3px',
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

function PanelInput({
  value,
  onChange,
  placeholder,
  mono,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  mono?: boolean;
}) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={{
        width: '100%',
        padding: '6px 8px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#334155',
        background: '#f8fafc',
        outline: 'none',
        fontFamily: mono ? 'ui-monospace, monospace' : 'inherit',
      }}
      onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
      onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
    />
  );
}

function DetailPanel({
  variable,
  dependsOn,
  dependedBy,
  isCircular,
  onClose,
  onNavigate,
  onSave,
  onDelete,
  isCreateMode,
  existingCodes,
  initialFormula,
  onEditingChange,
}: {
  variable: Variable | null;
  dependsOn: Variable[];
  dependedBy: Variable[];
  isCircular: boolean;
  onClose: () => void;
  onNavigate: (code: string) => void;
  onSave?: (data: {
    code: string;
    name: string;
    variableType: string;
    formula?: string;
    defaultValue?: string;
    description?: string;
  }) => Promise<void>;
  onDelete?: (id: string) => void;
  isCreateMode?: boolean;
  existingCodes?: string[];
  initialFormula?: string;
  onEditingChange?: (editing: boolean) => void;
}) {
  const [isEditing, setIsEditing] = useState(!!isCreateMode);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    code: variable?.code || '',
    name: variable?.name || '',
    variableType:
      variable?.variableType || (isCreateMode && initialFormula ? 'CALCULATED' : 'NUMBER'),
    formula: variable?.formula || initialFormula || '',
    defaultValue: variable?.defaultValue != null ? String(variable.defaultValue) : '',
    description: variable?.description || '',
  });

  const colors = variable
    ? TYPE_COLORS[variable.variableType] || TYPE_COLORS.TEXT
    : TYPE_COLORS.NUMBER;
  const Icon = variable ? TYPE_ICONS[variable.variableType] || Hash : Hash;

  const handleSave = async () => {
    if (!form.code || !form.name) return;
    setSaving(true);
    try {
      await onSave?.({
        code: form.code.toUpperCase(),
        name: form.name,
        variableType: form.variableType,
        formula: form.variableType === 'CALCULATED' ? form.formula : undefined,
        defaultValue: form.defaultValue || undefined,
        description: form.description || undefined,
      });
      if (isCreateMode) {
        onClose();
      } else {
        setIsEditing(false);
        onEditingChange?.(false);
      }
    } finally {
      setSaving(false);
    }
  };

  // Autocomplete hints for formula
  const availableCodes = (existingCodes || []).filter((c) => c !== form.code);

  return (
    <div
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        width: '320px',
        maxHeight: 'calc(100% - 20px)',
        overflowY: 'auto',
        background: 'white',
        borderRadius: '12px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.15)',
        zIndex: 20,
        fontSize: '13px',
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: '12px 16px',
          borderBottom: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: isCreateMode ? '#eff6ff' : isCircular ? '#fef2f2' : colors.bg,
          borderRadius: '12px 12px 0 0',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Icon
            size={16}
            style={{ color: isCreateMode ? '#3b82f6' : isCircular ? '#ef4444' : colors.text }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: '13px',
              color: isCreateMode ? '#3b82f6' : colors.text,
            }}
          >
            {isCreateMode
              ? 'Nueva variable'
              : isEditing
                ? `Editando ${variable?.code}`
                : variable?.code}
          </span>
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {!isEditing && !isCreateMode && onSave && (
            <button
              onClick={() => {
                setIsEditing(true);
                onEditingChange?.(true);
              }}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '6px',
                color: '#3b82f6',
              }}
              title="Editar"
            >
              <Edit size={14} />
            </button>
          )}
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '6px',
              color: '#94a3b8',
            }}
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '12px 16px' }}>
        {isEditing || isCreateMode ? (
          /* ─── Edit/Create mode ─── */
          <>
            <PanelField label="Código">
              <PanelInput
                value={form.code}
                onChange={(v) => setForm({ ...form, code: v.toUpperCase() })}
                placeholder="COSTO_USD"
                mono
              />
            </PanelField>

            <PanelField label="Nombre">
              <PanelInput
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Costo en dólares"
              />
            </PanelField>

            <PanelField label="Tipo">
              <SearchableSelect
                value={form.variableType}
                onValueChange={(val) => setForm({ ...form, variableType: val })}
                options={VAR_TYPE_OPTIONS.map((opt) => ({ value: opt.value, label: opt.label }))}
                placeholder="Seleccionar tipo..."
              />
            </PanelField>

            {form.variableType === 'CALCULATED' && (
              <PanelField label="Fórmula">
                <textarea
                  value={form.formula}
                  onChange={(e) => setForm({ ...form, formula: e.target.value })}
                  placeholder="COSTO_USD * TIPO_CAMBIO"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '6px 8px',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'ui-monospace, monospace',
                    color: '#334155',
                    background: '#f8fafc',
                    outline: 'none',
                    resize: 'vertical',
                  }}
                  onFocus={(e) => (e.target.style.borderColor = '#3b82f6')}
                  onBlur={(e) => (e.target.style.borderColor = '#e2e8f0')}
                />
                {availableCodes.length > 0 && (
                  <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                    {availableCodes.slice(0, 10).map((c) => (
                      <button
                        key={c}
                        onClick={() =>
                          setForm({ ...form, formula: form.formula ? `${form.formula} ${c}` : c })
                        }
                        style={{
                          padding: '1px 6px',
                          borderRadius: '4px',
                          border: '1px solid #e2e8f0',
                          background: '#f8fafc',
                          fontSize: '10px',
                          fontFamily: 'ui-monospace, monospace',
                          color: '#64748b',
                          cursor: 'pointer',
                        }}
                        title={`Insertar ${c}`}
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                )}
              </PanelField>
            )}

            {form.variableType !== 'CALCULATED' && (
              <PanelField label="Valor por defecto">
                <PanelInput
                  value={form.defaultValue}
                  onChange={(v) => setForm({ ...form, defaultValue: v })}
                  placeholder="0"
                  mono
                />
              </PanelField>
            )}

            <PanelField label="Descripción">
              <PanelInput
                value={form.description}
                onChange={(v) => setForm({ ...form, description: v })}
                placeholder="Opcional"
              />
            </PanelField>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '12px' }}>
              <button
                onClick={handleSave}
                disabled={saving || !form.code || !form.name}
                style={{
                  flex: 1,
                  padding: '7px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  background: saving || !form.code || !form.name ? '#94a3b8' : '#3b82f6',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {saving ? 'Guardando...' : isCreateMode ? 'Crear variable' : 'Guardar'}
              </button>
              <button
                onClick={() => {
                  if (isCreateMode) {
                    onClose();
                  } else {
                    setIsEditing(false);
                    onEditingChange?.(false);
                  }
                }}
                style={{
                  padding: '7px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  background: 'white',
                  color: '#64748b',
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                Cancelar
              </button>
            </div>
          </>
        ) : (
          /* ─── Read mode ─── */
          <>
            {/* Name + type */}
            <div style={{ color: '#475569', fontWeight: 500, marginBottom: '6px' }}>
              {variable?.name}
            </div>
            <span
              style={{
                display: 'inline-block',
                fontSize: '10px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                padding: '2px 8px',
                borderRadius: '4px',
                background: isCircular ? '#fee2e2' : `${colors.border}18`,
                color: isCircular ? '#dc2626' : colors.text,
                marginBottom: '12px',
              }}
            >
              {TYPE_LABELS[variable?.variableType || ''] || variable?.variableType}
              {variable?.isGlobal ? ' · Global' : ''}
            </span>

            {variable?.description && (
              <PanelField label="Descripción">
                <div style={{ color: '#334155', lineHeight: 1.5, fontSize: '12px' }}>
                  {variable.description}
                </div>
              </PanelField>
            )}

            {variable?.formula && (
              <PanelField label="Fórmula">
                <code
                  style={{
                    display: 'block',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    borderRadius: '6px',
                    padding: '8px 10px',
                    fontSize: '12px',
                    fontFamily: 'ui-monospace, monospace',
                    color: '#334155',
                    wordBreak: 'break-all',
                    lineHeight: 1.5,
                  }}
                >
                  {variable.formula}
                </code>
              </PanelField>
            )}

            {variable?.defaultValue != null && variable.defaultValue !== '' && (
              <PanelField label="Valor por defecto">
                <div
                  style={{
                    color: '#334155',
                    fontFamily: 'ui-monospace, monospace',
                    fontSize: '12px',
                  }}
                >
                  {String(variable.defaultValue)}
                </div>
              </PanelField>
            )}

            {/* Dependencies */}
            {dependsOn.length > 0 && (
              <PanelField label={`Depende de (${dependsOn.length})`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {dependsOn
                    .filter((d) => d.code)
                    .map((dep) => {
                      const dc = TYPE_COLORS[dep.variableType] || TYPE_COLORS.TEXT;
                      return (
                        <button
                          key={dep.code}
                          onClick={() => onNavigate(dep.code!)}
                          style={{
                            padding: '3px 8px',
                            borderRadius: '5px',
                            border: `1px solid ${dc.border}40`,
                            background: dc.bg,
                            color: dc.text,
                            fontSize: '11px',
                            fontFamily: 'ui-monospace, monospace',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {dep.code}
                        </button>
                      );
                    })}
                </div>
              </PanelField>
            )}

            {dependedBy.length > 0 && (
              <PanelField label={`Usado por (${dependedBy.length})`}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {dependedBy
                    .filter((d) => d.code)
                    .map((dep) => {
                      const dc = TYPE_COLORS[dep.variableType] || TYPE_COLORS.TEXT;
                      return (
                        <button
                          key={dep.code}
                          onClick={() => onNavigate(dep.code!)}
                          style={{
                            padding: '3px 8px',
                            borderRadius: '5px',
                            border: `1px solid ${dc.border}40`,
                            background: dc.bg,
                            color: dc.text,
                            fontSize: '11px',
                            fontFamily: 'ui-monospace, monospace',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {dep.code}
                        </button>
                      );
                    })}
                </div>
              </PanelField>
            )}

            {dependsOn.length === 0 && dependedBy.length === 0 && (
              <div
                style={{
                  color: '#94a3b8',
                  fontSize: '12px',
                  textAlign: 'center',
                  padding: '8px 0',
                }}
              >
                Variable independiente
              </div>
            )}

            {/* Delete button */}
            {onDelete && variable && (
              <div
                style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid #f1f5f9' }}
              >
                <button
                  onClick={() => onDelete(variable.id)}
                  style={{
                    width: '100%',
                    padding: '6px',
                    borderRadius: '6px',
                    border: '1px solid #fecaca',
                    background: 'white',
                    color: '#ef4444',
                    fontSize: '11px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                >
                  <X size={12} /> Eliminar variable
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── Legend (compact) ─────────────────────────────────────────────────

const LEGEND_ITEMS = [
  { label: 'Num / % / $', types: ['NUMBER', 'PERCENTAGE', 'CURRENCY'] },
  { label: 'Texto', types: ['TEXT'] },
  { label: 'Boolean', types: ['BOOLEAN'] },
  { label: 'Calculada', types: ['CALCULATED'] },
  { label: 'Enum', types: ['ENUM'] },
  { label: 'Escalonada', types: ['TIERED'] },
  { label: 'Lookup', types: ['LOOKUP'] },
];

function Legend() {
  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        padding: '8px 10px',
        fontSize: '10px',
        lineHeight: '1.8',
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: '2px', color: '#334155', fontSize: '11px' }}>
        Tipos
      </div>
      {LEGEND_ITEMS.map((item) => {
        const col = TYPE_COLORS[item.types[0]] || TYPE_COLORS.TEXT;
        return (
          <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <span
              style={{
                width: '10px',
                height: '10px',
                borderRadius: '3px',
                border: `2px solid ${col.border}`,
                background: col.bg,
                display: 'inline-block',
                flexShrink: 0,
              }}
            />
            <span style={{ color: '#475569' }}>{item.label}</span>
          </div>
        );
      })}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '5px',
          marginTop: '3px',
          paddingTop: '3px',
          borderTop: '1px solid #e2e8f0',
        }}
      >
        <span
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '3px',
            border: '2px solid #ef4444',
            background: '#fef2f2',
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
        <span style={{ color: '#ef4444', fontWeight: 600 }}>Circular</span>
      </div>
    </div>
  );
}

// ─── Inner Graph (needs ReactFlowProvider context) ────────────────────

function InnerGraph({
  variables,
  isFullscreen,
  onToggleFullscreen,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
  onRefresh,
}: {
  variables: Variable[];
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
  onAddVariable?: () => void;
  onRefresh?: () => void;
  onEditVariable?: (variable: Variable) => void;
  onDeleteVariable?: (variableId: string) => void;
}) {
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    variable: Variable;
  } | null>(null);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [createFromCode, setCreateFromCode] = useState<string | null>(null);
  const [editingCode, setEditingCode] = useState<string | null>(null);
  const { fitView, setCenter } = useReactFlow();

  // Build dependency maps
  const { codeToVar, depsOfCode, usedByCode, circularSet } = useMemo(() => {
    const codeMap = new Map(variables.filter((v) => v.code).map((v) => [v.code!, v]));

    const depsOf = new Map<string, string[]>();
    const usedBy = new Map<string, string[]>();

    for (const v of variables) {
      if (!v.code) continue;
      const deps = v.formula
        ? extractVariables(v.formula).filter((d) => codeMap.has(d) && d !== v.code)
        : [];
      depsOf.set(v.code, deps);
      for (const dep of deps) {
        const existing = usedBy.get(dep) || [];
        existing.push(v.code);
        usedBy.set(dep, existing);
      }
    }

    const circular = new Set(
      detectCircularDependencies(
        variables.filter((v): v is Variable & { code: string } => !!v.code)
      )
    );

    return { codeToVar: codeMap, depsOfCode: depsOf, usedByCode: usedBy, circularSet: circular };
  }, [variables]);

  // Get connected codes for selected node
  const connectedCodes = useMemo(() => {
    if (!selectedCode) return new Set<string>();
    const deps = depsOfCode.get(selectedCode) || [];
    const users = usedByCode.get(selectedCode) || [];
    return new Set([...deps, ...users]);
  }, [selectedCode, depsOfCode, usedByCode]);

  // Build nodes & edges
  const { nodes, edges } = useMemo(() => {
    const layers = computeLayerAssignment(variables);

    const layerGroups = new Map<number, string[]>();
    for (const v of variables) {
      if (!v.code) continue;
      const layer = layers.get(v.code) ?? 0;
      if (!layerGroups.has(layer)) layerGroups.set(layer, []);
      layerGroups.get(layer)!.push(v.code);
    }

    const graphNodes: Node<CustomNodeData>[] = variables
      .filter((v) => v.code)
      .map((v) => {
        const code = v.code!;
        const layer = layers.get(code) ?? 0;
        const layerMembers = layerGroups.get(layer) || [code];
        const indexInLayer = layerMembers.indexOf(code);
        const layerWidth = layerMembers.length * HORIZONTAL_SPACING;
        const offsetX = -layerWidth / 2 + HORIZONTAL_SPACING / 2;

        const isSelected = selectedCode === code;
        const isConnected = selectedCode != null && connectedCodes.has(code);
        const isDimmed = selectedCode != null && !isSelected && !isConnected;

        return {
          id: v.id,
          type: 'custom',
          data: {
            variable: v,
            isSelected,
            isConnected,
            isDimmed,
            isCircular: circularSet.has(code),
            isEditing: editingCode === code,
            dependsOn: depsOfCode.get(code) || [],
            dependedBy: usedByCode.get(code) || [],
          },
          position: {
            x: offsetX + indexInLayer * HORIZONTAL_SPACING,
            y: layer * VERTICAL_SPACING,
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        };
      });

    // Edges
    const graphEdges: Edge[] = [];
    let edgeId = 0;

    for (const v of variables) {
      if (!v.formula || !v.code) continue;
      const vCode = v.code;
      const deps = extractVariables(v.formula);

      for (const depCode of deps) {
        const depVar = codeToVar.get(depCode);
        if (!depVar) continue;

        const bothCircular = circularSet.has(vCode) && circularSet.has(depCode);
        const isHighlighted =
          selectedCode != null &&
          ((selectedCode === vCode && connectedCodes.has(depCode)) ||
            (selectedCode === depCode && connectedCodes.has(vCode)));
        const isDimmed = selectedCode != null && !isHighlighted;

        graphEdges.push({
          id: `edge-${edgeId++}`,
          source: depVar.id,
          target: v.id,
          animated: isHighlighted || bothCircular,
          style: {
            stroke: bothCircular
              ? '#ef4444'
              : isHighlighted
                ? TYPE_COLORS[v.variableType]?.border || '#3b82f6'
                : '#cbd5e1',
            strokeWidth: isHighlighted ? 2.5 : bothCircular ? 2 : 1.2,
            opacity: isDimmed ? 0.15 : 1,
            transition: 'all 0.25s ease',
          },
          markerEnd:
            isHighlighted || !isDimmed
              ? {
                  type: MarkerType.ArrowClosed,
                  color: bothCircular
                    ? '#ef4444'
                    : isHighlighted
                      ? TYPE_COLORS[v.variableType]?.border || '#3b82f6'
                      : '#cbd5e1',
                  width: 16,
                  height: 12,
                }
              : undefined,
        });
      }
    }

    return { nodes: graphNodes, edges: graphEdges };
  }, [
    variables,
    selectedCode,
    connectedCodes,
    codeToVar,
    depsOfCode,
    usedByCode,
    circularSet,
    editingCode,
  ]);

  // Event handlers
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node<CustomNodeData>) => {
    const code = node.data.variable.code;
    setSelectedCode((prev) => (prev === code ? null : (code ?? null)));
    setContextMenu(null);
  }, []);

  const onNodeDoubleClick = useCallback(
    (_: React.MouseEvent, node: Node<CustomNodeData>) => {
      onEditVariable?.(node.data.variable);
    },
    [onEditVariable]
  );

  const onNodeContextMenu = useCallback((event: React.MouseEvent, node: Node<CustomNodeData>) => {
    event.preventDefault();
    setContextMenu({ x: event.clientX, y: event.clientY, variable: node.data.variable });
  }, []);

  const onPaneClick = useCallback(() => {
    setSelectedCode(null);
    setContextMenu(null);
  }, []);

  const handleNavigate = useCallback(
    (code: string) => {
      setSelectedCode(code);
      const variable = codeToVar.get(code);
      if (!variable) return;
      const targetNode = nodes.find((n) => n.data.variable.code === code);
      if (targetNode) {
        setCenter(targetNode.position.x + 70, targetNode.position.y + 40, {
          zoom: 1.5,
          duration: 600,
        });
      }
    },
    [codeToVar, nodes, setCenter]
  );

  const handleFitView = useCallback(() => {
    fitView({ padding: 0.3, duration: 400 });
  }, [fitView]);

  // Selected variable data for detail panel
  const selectedVariable = selectedCode ? codeToVar.get(selectedCode) : null;
  const selectedDependsOn = selectedCode
    ? ((depsOfCode.get(selectedCode) || [])
        .map((c) => codeToVar.get(c))
        .filter(Boolean) as Variable[])
    : [];
  const selectedDependedBy = selectedCode
    ? ((usedByCode.get(selectedCode) || [])
        .map((c) => codeToVar.get(c))
        .filter(Boolean) as Variable[])
    : [];

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodeContextMenu={onNodeContextMenu}
        onPaneClick={onPaneClick}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        proOptions={{ hideAttribution: true }}
        minZoom={0.2}
        maxZoom={3}
        defaultEdgeOptions={{
          type: 'smoothstep',
        }}
      >
        <Background color="#f1f5f9" gap={20} size={1} />
        <Controls showInteractive={false} />
        <MiniMap
          nodeColor={(node) => {
            const data = node.data as CustomNodeData | undefined;
            if (!data?.variable) return '#6b7280';
            const colors = TYPE_COLORS[data.variable.variableType];
            return colors?.border || '#6b7280';
          }}
          maskColor="rgba(255, 255, 255, 0.7)"
          style={{ borderRadius: '8px', border: '1px solid #e2e8f0' }}
        />

        {/* Top-right toolbar */}
        <Panel position="top-right">
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              onClick={() => {
                setSelectedCode(null);
                setIsCreateMode(false);
                setCreateFromCode(null);
                onAddVariable?.();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                height: '32px',
                padding: '0 10px',
                background: '#3b82f6',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                color: 'white',
                fontSize: '12px',
                fontWeight: 600,
              }}
              title="Agregar variable"
            >
              <span style={{ fontSize: '16px', lineHeight: 1 }}>+</span>
              Variable
            </button>
            {onRefresh && (
              <button
                onClick={onRefresh}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  height: '32px',
                  padding: '0 10px',
                  background: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  color: '#64748b',
                  fontSize: '12px',
                }}
                title="Actualizar grafo"
              >
                ↻ Actualizar
              </button>
            )}
            <button
              onClick={handleFitView}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#64748b',
              }}
              title="Centrar grafo"
            >
              <Focus size={16} />
            </button>
            <button
              onClick={onToggleFullscreen}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                background: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                cursor: 'pointer',
                color: '#64748b',
              }}
              title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
            >
              {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            </button>
          </div>
        </Panel>

        {/* Legend */}
        <Panel position="bottom-left">
          <Legend />
        </Panel>

        {/* Hint */}
        {!selectedCode && (
          <Panel position="top-left">
            <div
              style={{
                background: 'rgba(255,255,255,0.9)',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '6px 10px',
                fontSize: '11px',
                color: '#94a3b8',
                lineHeight: 1.6,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Info size={12} />
                Click = ver detalles · Doble-click = editar · Click derecho = menú
              </div>
            </div>
          </Panel>
        )}
      </ReactFlow>

      {/* Context menu */}
      {contextMenu && (
        <div
          style={{
            position: 'fixed',
            top: contextMenu.y,
            left: contextMenu.x,
            zIndex: 100,
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2)',
            padding: '4px',
            minWidth: '160px',
            fontSize: '13px',
          }}
        >
          <div style={{ padding: '4px 8px', fontSize: '11px', color: '#94a3b8', fontWeight: 600 }}>
            {contextMenu.variable.code}
          </div>
          {onEditVariable && (
            <button
              onClick={() => {
                setEditingCode(contextMenu.variable.code || null);
                setSelectedCode(contextMenu.variable.code || null);
                setContextMenu(null);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                width: '100%',
                padding: '6px 8px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                borderRadius: '4px',
                color: '#334155',
                textAlign: 'left',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
            >
              <Edit size={14} /> Editar variable
            </button>
          )}
          <button
            onClick={() => {
              setContextMenu(null);
              setSelectedCode(null);
              setIsCreateMode(false);
              setCreateFromCode(null);
              onAddVariable?.();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              padding: '6px 8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              borderRadius: '4px',
              color: '#334155',
              textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
          >
            <span style={{ fontSize: '14px', width: '14px', textAlign: 'center' }}>+</span> Nueva
            que use{' '}
            <strong style={{ fontFamily: 'ui-monospace, monospace', color: '#3b82f6' }}>
              {contextMenu.variable.code}
            </strong>
          </button>
          {onDeleteVariable && (
            <>
              <div style={{ height: '1px', background: '#e2e8f0', margin: '4px 0' }} />
              <button
                onClick={() => {
                  onDeleteVariable(contextMenu.variable.id);
                  setContextMenu(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  width: '100%',
                  padding: '6px 8px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  color: '#ef4444',
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#fef2f2')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}
              >
                <X size={14} /> Eliminar
              </button>
            </>
          )}
        </div>
      )}

      {/* Detail panel overlay — edit or view */}
      {(selectedVariable || isCreateMode) && (
        <DetailPanel
          variable={isCreateMode ? null : selectedVariable!}
          dependsOn={selectedDependsOn}
          dependedBy={selectedDependedBy}
          isCircular={selectedVariable ? circularSet.has(selectedVariable.code ?? '') : false}
          onClose={() => {
            setSelectedCode(null);
            setIsCreateMode(false);
            setCreateFromCode(null);
            setEditingCode(null);
          }}
          onNavigate={handleNavigate}
          onSave={async (_data) => {
            if (isCreateMode) {
              setIsCreateMode(false);
              setCreateFromCode(null);
              onAddVariable?.();
            } else if (selectedVariable) {
              onEditVariable?.(selectedVariable);
              setSelectedCode(null);
              setEditingCode(null);
            }
          }}
          onDelete={(id) => {
            onDeleteVariable?.(id);
            setEditingCode(null);
          }}
          isCreateMode={isCreateMode}
          existingCodes={variables.filter((v) => v.code).map((v) => v.code!)}
          initialFormula={createFromCode || undefined}
          onEditingChange={(editing) =>
            setEditingCode(editing && selectedVariable?.code ? selectedVariable.code : null)
          }
        />
      )}
    </>
  );
}

// ─── Main Export ───────────────────────────────────────────────────────

export function DependencyGraph({
  variables,
  onFullscreenChange,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
  onRefresh,
}: DependencyGraphProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    const next = !isFullscreen;
    setIsFullscreen(next);
    onFullscreenChange?.(next);
  }, [isFullscreen, onFullscreenChange]);

  // Handle ESC key to exit fullscreen
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
        onFullscreenChange?.(false);
      }
    },
    [isFullscreen, onFullscreenChange]
  );

  if (variables.length === 0) {
    return (
      <div
        style={{
          height: '400px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#94a3b8',
          fontSize: '14px',
        }}
      >
        No hay variables para visualizar
      </div>
    );
  }

  const containerStyle: React.CSSProperties = isFullscreen
    ? {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 50,
        background: '#ffffff',
      }
    : {
        height: '500px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: '#ffffff',
        position: 'relative',
      };

  return (
    <div style={containerStyle} tabIndex={0} onKeyDown={handleKeyDown}>
      <ReactFlowProvider>
        <InnerGraph
          variables={variables}
          isFullscreen={isFullscreen}
          onToggleFullscreen={toggleFullscreen}
          onAddVariable={onAddVariable}
          onEditVariable={onEditVariable}
          onDeleteVariable={onDeleteVariable}
          onRefresh={onRefresh}
        />
      </ReactFlowProvider>
    </div>
  );
}

export default DependencyGraph;
